import { Button, Empty, Flex, Modal, Table, Typography, Upload, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { RcFile } from 'antd/es/upload';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import {
  DownOutlined,
  EditOutlined,
  FileOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { useAdminObligationFileController_create } from '@api-client/generated/AdminObligationFileController/create';
import { useAdminObligationFileController_delete } from '@api-client/generated/AdminObligationFileController/delete';
import { useAdminObligationFileController_updateOneById } from '@api-client/generated/AdminObligationFileController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { Loader } from '@components';
import { useCurrentCompanyId } from '@hooks';
import { withFreshPreview } from '@utils';

import ObligationFilesPanel from './ObligationFilesPanel';
import TaxFilePreviewModal from './TaxFilePreviewModal';
import { formatDateToString, formatTaxName, formatTaxStatus } from './helpers';

type Obligation = Schemas.AdminObligationResponseDto;
type ObligationFile = Schemas.AdminObligationFileResponseDto;

type TaxTableProps = {
  taxesInGroup: Obligation[];
  isLoading: boolean;
  groupTitle: string;
  year: number;
  onOpenEditModal: (tax: Obligation) => void;
  onRefetch: () => void;
};

const getLatestStatusUser = (
  events: Schemas.AdminObligationResponseDto['events']
) => {
  if (!events || events.length === 0) return null;
  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const latestEvent = sortedEvents[0];
  if (latestEvent) {
    const date = dayjs(latestEvent.createdAt).format('DD/MM/YYYY HH:mm');
    return `${latestEvent.user.fullName} at ${date}`;
  }
  return null;
};

const TaxTable = (props: TaxTableProps) => {
  const companyId = useCurrentCompanyId();

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<ObligationFile | null>(null);

  // The previewed file is a snapshot frozen at click time; when the parent's
  // obligation list is re-fetched (polling a pending HEIC preview), reconcile the
  // open preview's fields against the fresh copy so a `pending → ready` transition
  // reaches the modal without a manual reload.
  useEffect(() => {
    setPreviewFile((prev) => {
      if (!prev) return prev;
      const fresh = props.taxesInGroup
        .flatMap((tax) => tax.files)
        .find((file) => file.id === prev.id);
      return withFreshPreview(prev, fresh);
    });
  }, [props.taxesInGroup]);

  const { mutateAsync: createFile } = useAdminObligationFileController_create();
  const { mutateAsync: updateFile } =
    useAdminObligationFileController_updateOneById();
  const { mutateAsync: deleteFile } = useAdminObligationFileController_delete();

  const [modal, contextHolder] = Modal.useModal();

  const expandRow = (id: string) =>
    setExpandedRowKeys((prev) => (prev.includes(id) ? prev : [...prev, id]));

  const toggleRow = (id: string) =>
    setExpandedRowKeys((prev) =>
      prev.includes(id) ? prev.filter((key) => key !== id) : [...prev, id]
    );

  const handleUploadFile = async (obligation: Obligation, file: RcFile) => {
    setUploadingId(obligation.id);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('obligationId', obligation.id);

      await createFile({
        parameter: { companyId },
        // @ts-expect-error formData is not typed
        requestBody: formData,
      });

      message.success(`"${file.name}" uploaded`);
      expandRow(obligation.id);
      await props.onRefetch();
    } catch (error) {
      console.error('Failed to upload file:', error);
      message.error(`Failed to upload "${file.name}"`);
    } finally {
      setUploadingId(null);
    }
  };

  const handleRenameFile = async (fileId: string, name: string) => {
    try {
      await updateFile({
        parameter: { id: fileId, companyId },
        requestBody: { name },
      });
      await props.onRefetch();
    } catch (error) {
      console.error('Failed to rename file:', error);
      message.error('Failed to rename file');
      // Re-throw so the panel keeps edit mode open instead of clearing it.
      throw error;
    }
  };

  const handleDeleteFile = (file: ObligationFile) => {
    modal.confirm({
      title: 'Are you sure you want to delete this file?',
      content: file.name || file.fileName,
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteFile({ parameter: { id: file.id, companyId } });
          await props.onRefetch();
        } catch (error) {
          console.error('Failed to delete file:', error);
          message.error('Failed to delete file');
          // Re-throw so the confirm modal stays open on failure.
          throw error;
        }
      },
    });
  };

  const columns: ColumnsType<Obligation> = [
    {
      title: 'Name',
      key: 'name',
      width: 300,
      render: (_: unknown, record: Obligation) => (
        <strong>{formatTaxName(record.type, record.period)}</strong>
      ),
    },
    {
      title: 'Due Date',
      key: 'dueDate',
      width: 150,
      render: (_: unknown, record: Obligation) =>
        formatDateToString(record.dueDate),
    },
    {
      title: 'Status',
      key: 'status',
      width: 300,
      render: (_: unknown, record: Obligation) => {
        const { name, color } = formatTaxStatus(record.status);
        return (
          <Flex vertical>
            <Flex vertical>
              <Typography.Text type={color} strong>
                {name}
              </Typography.Text>
              {getLatestStatusUser(record.events)}
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      width: 200,
      render: (_: unknown, record: Obligation) => {
        if (!record.isPayable) return '-';

        return (
          <Flex vertical>
            <span>
              {record.status === 'paid' || record.status === 'done'
                ? 'Paid'
                : 'Unpaid'}
            </span>
            <span>Due: {formatDateToString(record.paymentDueDate)}</span>
          </Flex>
        );
      },
    },
    {
      title: 'Files',
      key: 'files',
      width: 220,
      render: (_: unknown, record: Obligation) => {
        const filesCount = record.files?.length || 0;
        const isExpanded = expandedRowKeys.includes(record.id);

        return (
          <Flex gap={8} align="center">
            {filesCount > 0 && (
              <Button
                type="secondary"
                icon={<FileOutlined />}
                onClick={() => toggleRow(record.id)}
              >
                {filesCount > 1 ? `${filesCount} files` : '1 file'}
                {isExpanded ? <UpOutlined /> : <DownOutlined />}
              </Button>
            )}

            <Upload
              multiple
              showUploadList={false}
              beforeUpload={(file) => {
                handleUploadFile(record, file);
                return false;
              }}
            >
              <Button
                type={filesCount > 0 ? 'link' : 'secondary'}
                icon={<PlusOutlined />}
                loading={uploadingId === record.id}
              >
                {filesCount > 0 ? 'Add file' : 'Add files'}
              </Button>
            </Upload>
          </Flex>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, record: Obligation) => (
        <Flex gap={8}>
          <Button
            type="secondary"
            icon={<EditOutlined />}
            onClick={() => props.onOpenEditModal(record)}
          >
            Edit
          </Button>
        </Flex>
      ),
    },
  ];

  if (props.isLoading) {
    return <Loader />;
  }

  if (props.taxesInGroup.length === 0) {
    return (
      <Empty
        description={`No ${props.groupTitle.toLowerCase()} found for ${props.year}`}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <>
      <Table
        dataSource={props.taxesInGroup}
        columns={columns}
        rowKey="id"
        pagination={false}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys,
          rowExpandable: (record) => (record.files?.length || 0) > 0,
          expandedRowRender: (record) => (
            <ObligationFilesPanel
              files={record.files || []}
              onPreview={setPreviewFile}
              onRename={handleRenameFile}
              onDelete={handleDeleteFile}
            />
          ),
        }}
      />

      <TaxFilePreviewModal
        file={previewFile}
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
      />

      {contextHolder}
    </>
  );
};

export default TaxTable;
